/**
 * Quick View — a STANDALONE product modal opened from any product card.
 *
 * This component owns its ENTIRE shell (overlay, panel, open/close, Esc,
 * focus trap, focus restore, body scroll-lock, enter/leave animation). It does
 * NOT depend on the shared ui-sheet system (window.UISheet / ui-sheet.* / the
 * ui-sheet__* classes). All markup uses `qv-*` classes and reads its config
 * from window.UntamedQuickView (emitted by snippets/css-variables.liquid).
 *
 * Desktop = split modal (media stage left + scrolling content right + sticky
 * footer). Mobile = bottom sheet (grip, media, content, sticky footer). Same
 * DOM; quick-view.css drives the two layouts responsively.
 *
 * Triggers (kept identical so existing product cards keep working):
 *   [data-quick-view]       — explicit pill/button, with data-product-handle
 *   [data-quick-view-card]  — whole-card tap, with data-product-handle
 *
 * Fetches /products/{handle}.js on open (prefetched on pointer/focus/touch
 * intent), caches it, and renders media + variant pickers + ATC. Add-to-cart
 * goes through window.CartAPI.addItem — the cart drawer opens itself on
 * cart:item-added; QV just closes on success.
 */
(function () {
  'use strict';

  var dom = null;
  var cache = {};
  var inflight = {};
  var state = null;
  var lastFocus = null;

  // ---- config (from window.UntamedQuickView, with safe fallbacks) ----
  function cfg() {
    return window.UntamedQuickView || {};
  }

  function ic(n) { return '<i class="ph-bold ' + n + '"></i>'; }
  function icf(n) { return '<i class="ph-fill ' + n + '"></i>'; }

  // Normalize injected Phosphor icons to the theme's configured weight class
  // (the hardcoded ph-bold / ph-fill only render if that weight stylesheet is
  // loaded; UntamedIcons.refresh adds the active weight class so glyphs show).
  function paintIcons() {
    if (window.UntamedIcons && dom) window.UntamedIcons.refresh(dom.panel);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
    });
  }

  function money(cents) {
    if (window.formatMoney && window.themeMoneyFormat) {
      try { return window.formatMoney(cents, window.themeMoneyFormat); } catch (e) {}
    }
    return '$' + (cents / 100).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }

  function isColor(name) { return /colou?r/i.test(name || ''); }

  function swatchColor(name) {
    // Prefer the global, brand-tuned color map shared with the product page.
    var ucs = window.UntamedColorSwatches;
    if (ucs && typeof ucs.resolve === 'function') {
      var hex = ucs.resolve(name);
      if (hex) return hex;
    }
    // Last-resort literal CSS color fall-through; the global hydrator will
    // back-fill anything else via api.color.pizza using data-color-name.
    var n = String(name).trim().toLowerCase().replace(/\s+/g, '');
    return /^[a-z]+$/.test(n) ? n : '';
  }

  // Strip HTML + collapse whitespace, then truncate to `max` chars on a word
  // boundary. Used for the lede (product.description).
  function plain(html, max) {
    var tmp = document.createElement('div');
    tmp.innerHTML = String(html || '');
    var txt = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
    if (max && txt.length > max) {
      txt = txt.slice(0, max);
      var lastSpace = txt.lastIndexOf(' ');
      if (lastSpace > max * 0.6) txt = txt.slice(0, lastSpace);
      txt = txt.replace(/[\s,.;:]+$/, '') + '…';
    }
    return txt;
  }

  // ---- highlights / rating extraction from product JSON ----
  // Highlights may live on a few common metafield shapes. We look for an array
  // or a newline/pipe-delimited string; return [] when nothing is present.
  function extractHighlights(p) {
    var mf = p.metafields || {};
    var cands = [
      p.highlights,
      mf.highlights,
      mf.custom && mf.custom.highlights,
      mf.custom && mf.custom.features,
      mf.descriptors && mf.descriptors.highlights
    ];
    for (var i = 0; i < cands.length; i++) {
      var c = cands[i];
      if (!c) continue;
      if (Array.isArray(c)) return c.map(String).filter(Boolean).slice(0, 5);
      if (typeof c === 'object' && c.value != null) c = c.value;
      if (typeof c === 'string') {
        var parts = c.split(/\r?\n|\||•/).map(function (s) { return s.trim(); }).filter(Boolean);
        if (parts.length) return parts.slice(0, 5);
      }
    }
    return [];
  }

  // Rating: only shown when real data exists on the product JSON. Supports a few
  // shapes (a plain rating/rating_count, or a metafield rating object).
  function extractRating(p) {
    var mf = p.metafields || {};
    var value = null, count = null;
    var rv = p.rating != null ? p.rating : (mf.reviews && mf.reviews.rating) || (mf.rating && mf.rating.value != null ? mf.rating.value : mf.rating);
    var rc = p.rating_count != null ? p.rating_count : (mf.reviews && mf.reviews.rating_count) || (mf.rating && mf.rating.count);
    // rating metafield can be a JSON object like { value, scale_max }
    if (rv && typeof rv === 'object') {
      if (rv.value != null) rv = rv.value;
    }
    value = parseFloat(rv);
    count = parseInt(rc, 10);
    if (!value || isNaN(value)) return null;
    return { value: value, count: isNaN(count) ? null : count };
  }

  // ---------------------------------------------------------------------------
  // Shell — built ONCE. Owns overlay + panel; no ui-sheet dependency.
  // ---------------------------------------------------------------------------
  function build() {
    if (dom) return dom;
    var overlay = document.createElement('div');
    overlay.className = 'qv-overlay';
    overlay.setAttribute('data-qv-overlay', '');
    overlay.setAttribute('aria-hidden', 'true');

    var panel = document.createElement('div');
    panel.className = 'qv-panel';
    panel.setAttribute('data-qv-panel', '');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Quick view');
    panel.setAttribute('aria-hidden', 'true');
    // No close button — the shopper clicks the overlay (or presses Esc) to close.
    panel.innerHTML =
      '<span class="qv-grip" aria-hidden="true"></span>' +
      '<div class="qv-body" data-qv-root></div>';

    document.body.appendChild(overlay);
    document.body.appendChild(panel);
    dom = { overlay: overlay, panel: panel, body: panel.querySelector('[data-qv-root]') };

    overlay.addEventListener('click', close);
    return dom;
  }

  // ---- open / close (self-managed) ----
  function open(handle, source) {
    build();
    lastFocus = document.activeElement;
    emit('quick_view_opened', { product_handle: handle, source: source || 'card_tap' });

    if (cache[handle]) {
      render(cache[handle]);                 // instant — already prefetched
    } else {
      dom.body.innerHTML = skeleton();
      fetchProduct(handle).then(function (p) {
        if (dom.panel.getAttribute('aria-hidden') === 'false') render(p);
      }).catch(function () {
        dom.body.innerHTML =
          '<div class="qv-loading">Couldn’t load this item.<br>' +
          '<a class="qv-btn qv-btn--secondary" href="/products/' + esc(handle) + '">View full details</a></div>';
      });
    }

    dom.overlay.setAttribute('aria-hidden', 'false');
    dom.panel.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('qv-open');
    document.addEventListener('keydown', onKeydown, true);

    requestAnimationFrame(function () {
      var f = dom.panel.querySelector('[data-qv-autofocus]') || dom.panel;
      if (f) try { f.focus(); } catch (e) {}
    });
  }

  function close() {
    if (!dom || dom.panel.getAttribute('aria-hidden') === 'true') return;
    dom.overlay.setAttribute('aria-hidden', 'true');
    dom.panel.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('qv-open');
    document.removeEventListener('keydown', onKeydown, true);
    if (state) { emit('quick_view_closed', { product_handle: state.product.handle }); state = null; }
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    lastFocus = null;
  }

  // Esc to close + focus trap within the panel.
  function onKeydown(e) {
    if (!dom || dom.panel.getAttribute('aria-hidden') === 'true') return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var focusables = dom.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var visible = [];
    for (var i = 0; i < focusables.length; i++) {
      var el = focusables[i];
      if (el.offsetParent !== null || el === document.activeElement) visible.push(el);
    }
    if (!visible.length) return;
    var first = visible[0], last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // ---- data ----
  function fetchProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (inflight[handle]) return inflight[handle];
    inflight[handle] = fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('fetch'); return r.json(); })
      .then(function (p) { cache[handle] = p; delete inflight[handle]; return p; })
      .catch(function (e) { delete inflight[handle]; throw e; });
    return inflight[handle];
  }

  function prefetch(handle) {
    if (!handle || cache[handle] || inflight[handle]) return;
    fetchProduct(handle).catch(function () {});
  }

  // Loading skeleton — mirrors the split layout so the panel has structure the
  // instant it opens, then crossfades to content.
  function skeleton() {
    return '<div class="qv-skeleton" data-qv-skeleton>' +
      '<div class="qv-skeleton__media sk-shimmer"></div>' +
      '<div class="qv-skeleton__content">' +
        '<span class="sk-line sk-shimmer" style="width:32%;height:11px"></span>' +
        '<span class="sk-line sk-shimmer" style="width:78%;height:24px;margin-top:12px"></span>' +
        '<span class="sk-line sk-shimmer" style="width:40%;height:22px;margin-top:16px"></span>' +
        '<div class="qv-skeleton__row">' +
          '<span class="sk-dot sk-shimmer"></span><span class="sk-dot sk-shimmer"></span><span class="sk-dot sk-shimmer"></span>' +
        '</div>' +
        '<div class="qv-skeleton__row">' +
          '<span class="sk-chip sk-shimmer"></span><span class="sk-chip sk-shimmer"></span><span class="sk-chip sk-shimmer"></span>' +
        '</div>' +
        '<span class="sk-line sk-shimmer" style="width:100%;height:52px;margin-top:auto;border-radius:12px"></span>' +
      '</div>' +
    '</div>';
  }

  function emit(name, detail) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, detail || {}));
      if (window.Shopify && window.Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, detail || {});
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  function render(p) {
    var firstAvail = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
    var chosen = (firstAvail.options || []).slice();
    var media = (p.media && p.media.length) ? p.media : (p.images || []).map(function (src) { return { src: src, media_type: 'image' }; });
    state = {
      product: p,
      chosen: chosen,
      mediaIndex: 0,
      qty: 1,
      media: media,
      rating: extractRating(p),
      highlights: extractHighlights(p),
      wished: false
    };
    if (window.Wishlist && typeof window.Wishlist.has === 'function') {
      try { state.wished = !!window.Wishlist.has(p.id); } catch (e) {}
    }

    dom.panel.classList.toggle('qv-panel--centered', cfg().layout === 'centered');

    // Single scroll flow: media + all product info scroll together. The ONLY
    // fixed element is the Add-to-cart button (renderBuyBar).
    dom.body.innerHTML =
      '<div class="qv-scroll" data-qv-scroll>' +
        '<div class="qv-media" data-qv-media></div>' +
        '<div class="qv-info" data-qv-info></div>' +
      '</div>' +
      '<div class="qv-buybar" data-qv-buybar></div>';

    renderMedia();
    renderContent();
    renderBuyBar();
    paintIcons();
    revealContent();
  }

  // Entrance animation via GSAP; CSS transition on the panel is the fallback.
  function revealContent() {
    if (matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var mediaEl = dom.body.querySelector('[data-qv-media]');
    var contentEls = dom.body.querySelectorAll('.qv-scroll > *');
    // Guarantee the final visible state even if GSAP never runs to completion
    // (missing lib, or a throttled rAF in a backgrounded tab). We animate plain
    // `opacity` (NOT autoAlpha — that strands elements at visibility:hidden), and
    // a safety timer clears any inline styles so content is always shown.
    function settle() {
      if (mediaEl) { mediaEl.style.opacity = ''; mediaEl.style.transform = ''; }
      contentEls.forEach(function (el) { el.style.opacity = ''; el.style.transform = ''; });
    }
    function run(gsap) {
      if (!gsap) { settle(); return; }
      if (mediaEl) gsap.fromTo(mediaEl, { opacity: 0, scale: 1.02 }, { opacity: 1, scale: 1, duration: 0.5, ease: 'power2.out', clearProps: 'opacity,transform' });
      if (contentEls.length) gsap.fromTo(contentEls, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out', stagger: 0.05, delay: 0.05, clearProps: 'opacity,transform' });
    }
    if (window.gsap) run(window.gsap);
    else if (window.gsapReady && window.gsapReady.then) window.gsapReady.then(run).catch(settle);
    else settle();
    setTimeout(settle, 900); // hard fallback — never leave content hidden
  }

  function currentVariant() {
    var p = state.product;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i], ok = true;
      for (var k = 0; k < state.chosen.length; k++) {
        if (String(v.options[k]) !== String(state.chosen[k])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  function valueAvailable(idx, val) {
    var p = state.product;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (!v.available) continue;
      if (String(v.options[idx]) !== String(val)) continue;
      var ok = true;
      for (var k = 0; k < p.options.length; k++) {
        if (k === idx) continue;
        if (String(v.options[k]) !== String(state.chosen[k])) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  // ---- media stage ----
  function renderMedia() {
    var wrap = dom.body.querySelector('[data-qv-media]');
    var media = state.media;
    var C = cfg();
    if (!media.length) { wrap.innerHTML = ''; return; }
    var i = state.mediaIndex;
    var m = media[i];
    var mainSrc = m.src || (m.preview_image && m.preview_image.src) || m;
    var isVid = m.media_type === 'video';

    var badge = '';
    if (C.showBadge !== false) {
      var v0 = currentVariant();
      var onSale = v0 && v0.compare_at_price > v0.price;
      if (state.product.tags && indexOfTag(state.product.tags, 'bestseller') > -1) badge = 'Bestseller';
      else if (onSale) badge = 'Sale';
      else if (isNew(state.product)) badge = 'New';
    }

    var showThumbs = C.showThumbnails !== false && media.length > 1;

    wrap.innerHTML =
      '<div class="qv-stage">' +
        (badge ? '<span class="qv-badge">' + esc(badge) + '</span>' : '') +
        '<button type="button" class="qv-heart' + (state.wished ? ' on' : '') + '" data-qv-heart aria-label="Save"' +
          ' aria-pressed="' + (state.wished ? 'true' : 'false') + '">' + icf('ph-heart') + '</button>' +
        (isVid
          ? '<video src="' + esc((m.sources && m.sources[0] && m.sources[0].url) || mainSrc) + '" muted playsinline loop autoplay></video>'
          : '<img src="' + esc(mainSrc) + '" alt="">') +
        (media.length > 1
          ? '<button type="button" class="qv-arrow prev" data-qv-prev aria-label="Previous">' + ic('ph-caret-left') + '</button>' +
            '<button type="button" class="qv-arrow next" data-qv-next aria-label="Next">' + ic('ph-caret-right') + '</button>' +
            '<span class="qv-count"><b>' + (i + 1) + '</b> / ' + media.length + '</span>'
          : '') +
      '</div>' +
      (showThumbs
        ? '<div class="qv-thumbs">' + media.map(function (mm, idx) {
            var t = (mm.preview_image && mm.preview_image.src) || mm.src || mm;
            return '<button type="button" class="qv-thumb' + (idx === i ? ' on' : '') + '" data-qv-thumb="' + idx + '" aria-label="View image ' + (idx + 1) + '"><img src="' + esc(t) + '" alt=""></button>';
          }).join('') + '</div>'
        : '');

    var prev = wrap.querySelector('[data-qv-prev]');
    var next = wrap.querySelector('[data-qv-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    wrap.querySelectorAll('[data-qv-thumb]').forEach(function (t) {
      t.addEventListener('click', function () { state.mediaIndex = parseInt(t.getAttribute('data-qv-thumb'), 10); renderMedia(); paintIcons(); });
    });
    var heart = wrap.querySelector('[data-qv-heart]');
    if (heart) heart.addEventListener('click', toggleWish);
    paintIcons();
  }

  function step(dir) {
    var n = state.media.length;
    state.mediaIndex = (state.mediaIndex + dir + n) % n;
    renderMedia();
    paintIcons();
  }

  function indexOfTag(tags, needle) {
    if (!tags) return -1;
    var arr = Array.isArray(tags) ? tags : String(tags).split(',');
    for (var i = 0; i < arr.length; i++) if (String(arr[i]).trim().toLowerCase() === needle) return i;
    return -1;
  }
  function isNew(p) {
    if (!p.published_at) return false;
    var days = (Date.now() - new Date(p.published_at).getTime()) / 86400000;
    return days >= 0 && days <= 30;
  }

  function toggleWish() {
    var p = state.product;
    state.wished = !state.wished;
    var heart = dom.body.querySelector('[data-qv-heart]');
    if (heart) { heart.classList.toggle('on', state.wished); heart.setAttribute('aria-pressed', state.wished ? 'true' : 'false'); }
    if (window.Wishlist && typeof window.Wishlist.toggle === 'function') {
      var v = currentVariant();
      try {
        window.Wishlist.toggle({
          id: p.id,
          handle: p.handle,
          title: p.title,
          url: p.url,
          price: v ? v.price : p.price,
          image: p.featured_image || (p.images && p.images[0]),
          variantId: v ? v.id : null
        });
      } catch (e) {}
    }
  }

  function syncMediaToVariant() {
    var v = currentVariant();
    if (!v || v.featured_image == null || !state.media) return;
    var fid = v.featured_image.id;
    for (var i = 0; i < state.media.length; i++) {
      if (state.media[i].id === fid) { state.mediaIndex = i; renderMedia(); paintIcons(); break; }
    }
  }

  // ---- scrolling content ----
  function renderContent() {
    var p = state.product;
    var C = cfg();
    var wrap = dom.body.querySelector('[data-qv-info]');

    // Eyebrow: vendor or type per config.
    var eyebrow = '';
    if (C.eyebrow === 'vendor' && p.vendor) eyebrow = p.vendor;
    else if (C.eyebrow === 'type' && p.type) eyebrow = p.type;

    // Rating (only if real data exists).
    var ratingHtml = '';
    if (C.showRating !== false && state.rating) {
      var full = Math.round(state.rating.value);
      var stars = '';
      for (var s = 0; s < 5; s++) stars += (s < full ? icf('ph-star') : ic('ph-star'));
      ratingHtml =
        '<div class="qv-rating">' +
          '<span class="qv-stars">' + stars + '</span>' +
          '<span class="qv-rating-val">' + (Math.round(state.rating.value * 10) / 10) + '</span>' +
          (state.rating.count != null ? '<span class="qv-rating-count">' + state.rating.count + ' review' + (state.rating.count === 1 ? '' : 's') + '</span>' : '') +
        '</div>';
    }

    // Lede.
    var ledeHtml = '';
    if (C.showLede && p.description) {
      var lede = plain(p.description, C.ledeMax || 180);
      if (lede) ledeHtml = '<p class="qv-lede">' + esc(lede) + '</p>';
    }

    // Highlights (only if present).
    var featsHtml = '';
    if (C.showHighlights !== false && state.highlights.length) {
      featsHtml = '<div class="qv-feats">' + state.highlights.map(function (h) {
        return '<div class="qv-feat"><span class="qv-tick">' + ic('ph-check') + '</span>' + esc(h) + '</div>';
      }).join('') + '</div>';
    }

    var showQty = C.showQty !== false;
    var qtyHtml = showQty
      ? '<div class="qv-qtyfield"><span class="qv-qtylabel">Quantity</span>' +
          '<div class="qv-qty"><button type="button" data-qv-qty="-1" aria-label="Decrease">' + ic('ph-minus') + '</button>' +
          '<span class="v" data-qv-qtyval>' + state.qty + '</span>' +
          '<button type="button" data-qv-qty="1" aria-label="Increase">' + ic('ph-plus') + '</button></div>' +
        '</div>'
      : '';

    wrap.innerHTML =
      (eyebrow ? '<div class="qv-eyebrow">' + esc(eyebrow) + '</div>' : '') +
      '<h2 class="qv-title">' + esc(p.title) + '</h2>' +
      ratingHtml +
      '<div class="qv-price" data-qv-price></div>' +
      ledeHtml +
      featsHtml +
      (eyebrow || ratingHtml || ledeHtml || featsHtml ? '<div class="qv-rule"></div>' : '') +
      '<div class="qv-options" data-qv-options></div>' +
      '<div class="qv-stock" data-qv-stock hidden></div>' +
      qtyHtml +
      // View-details lives in the fixed buy bar (renderBuyBar); trust line hidden.
      '<div class="qv-error" data-qv-error hidden></div>';

    paintPrice();
    renderOptions();
    renderStock();

    wrap.querySelectorAll('[data-qv-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = parseInt(btn.getAttribute('data-qv-qty'), 10);
        state.qty = Math.max(1, state.qty + d);
        var q = wrap.querySelector('[data-qv-qtyval]'); if (q) q.textContent = state.qty;
        paintTotal();
      });
    });
    paintIcons();
  }

  function renderOptions() {
    var p = state.product;
    var host = dom.body.querySelector('[data-qv-options]');
    var groups = (p.options || []).map(function (opt, idx) {
      if (p.options.length === 1 && opt.name === 'Title' && (opt.values || []).length === 1 && opt.values[0] === 'Default Title') return null;
      var color = isColor(opt.name);
      if (color) {
        var swatches = opt.values.map(function (val) {
          var sel = String(state.chosen[idx]) === String(val);
          var avail = valueAvailable(idx, val);
          var hex = swatchColor(val);
          return '<button type="button" class="qv-swatch' + (sel ? ' on' : '') + '"' +
            (avail ? '' : ' disabled') +
            ' data-qv-oi="' + idx + '" data-qv-val="' + esc(val) + '" data-color-name="' + esc(val) + '"' +
            ' style="background:' + esc(hex || '#ccc') + '" aria-label="' + esc(val) + '"' +
            (sel ? ' aria-current="true"' : '') + '></button>';
        }).join('');
        return '<div class="qv-optgroup">' +
          '<div class="qv-optlabel"><span class="k">' + esc(opt.name) + ' <em data-qv-optval="' + idx + '">' + esc(state.chosen[idx]) + '</em></span></div>' +
          '<div class="qv-swatches">' + swatches + '</div>' +
          '</div>';
      }
      var pills = opt.values.map(function (val) {
        var sel = String(state.chosen[idx]) === String(val);
        var avail = valueAvailable(idx, val);
        return '<button type="button" class="qv-pill' + (sel ? ' on' : '') + '"' +
          (avail ? '' : ' disabled') +
          ' data-qv-oi="' + idx + '" data-qv-val="' + esc(val) + '"' +
          (sel ? ' aria-current="true"' : '') + '>' + esc(val) + '</button>';
      }).join('');
      return '<div class="qv-optgroup">' +
        '<div class="qv-optlabel"><span class="k">' + esc(opt.name) + '</span></div>' +
        '<div class="qv-pills">' + pills + '</div>' +
        '</div>';
    }).filter(Boolean).join('');

    host.innerHTML = groups;
    host.querySelectorAll('[data-qv-val]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var idx = parseInt(btn.getAttribute('data-qv-oi'), 10);
        state.chosen[idx] = btn.getAttribute('data-qv-val');
        emit('quick_view_variant_changed', { product_handle: state.product.handle });
        renderOptions();
        paintPrice();
        renderStock();
        renderFoot();
        syncMediaToVariant();
        paintIcons();
      });
    });
    paintIcons();
  }

  function renderStock() {
    var C = cfg();
    var el = dom.body.querySelector('[data-qv-stock]');
    if (!el) return;
    var v = currentVariant();
    var threshold = C.stockThreshold || 5;
    if (C.showStock === false || !v || !v.available || typeof v.inventory_quantity !== 'number' || v.inventory_quantity <= 0 || v.inventory_quantity > threshold) {
      el.hidden = true; el.innerHTML = ''; return;
    }
    el.hidden = false;
    el.innerHTML = '<span class="d"></span><span>Only ' + v.inventory_quantity + ' left</span>';
  }

  function paintPrice() {
    var C = cfg();
    var v = currentVariant();
    var el = dom.body.querySelector('[data-qv-price]');
    if (!el) return;
    if (!v) { el.innerHTML = '<span class="now">—</span>'; return; }
    var onSale = v.compare_at_price > v.price;
    var installments = '';
    if (C.showInstallments && (C.installmentCount || 4) > 1) {
      var n = C.installmentCount || 4;
      installments = '<span class="qv-pay">or ' + n + ' × <b>' + money(Math.round(v.price / n)) + '</b> interest-free</span>';
    }
    el.innerHTML =
      '<span class="now">' + money(v.price) + '</span>' +
      (onSale ? '<span class="was">' + money(v.compare_at_price) + '</span><span class="save">Save ' + money(v.compare_at_price - v.price) + '</span>' : '') +
      installments;
  }

  // ---- floating buy bar (the ONLY fixed element) ----
  // Two buttons, 50/50: "View details" (link to the PDP) + "Add to cart"
  // (no price in the button; sold-out state still disables it).
  function renderBuyBar() {
    var C = cfg();
    var p = state.product;
    var bar = dom.body.querySelector('[data-qv-buybar]');

    bar.innerHTML =
      '<a class="qv-view-btn" href="' + esc(p.url) + '">' + esc(C.viewFull || 'View details') + '</a>' +
      '<button type="button" class="qv-buy" data-qv-atc data-qv-autofocus>' +
        '<span class="qv-buy-label">' + esc(C.atc || 'Add to cart') + '</span>' +
      '</button>';

    paintTotal();
    var atc = bar.querySelector('[data-qv-atc]');
    if (atc) atc.addEventListener('click', addToCart);
    paintIcons();
  }

  // Sold-out / disabled state for the buy button (no price shown).
  function paintTotal() {
    var bar = dom.body.querySelector('[data-qv-buybar]');
    if (!bar) return;
    var v = currentVariant();
    var atc = bar.querySelector('[data-qv-atc]');
    var label = bar.querySelector('.qv-buy-label');
    var C = cfg();
    if (!atc) return;
    if (!v || !v.available) {
      atc.disabled = true;
      if (label) label.textContent = v ? 'Sold out' : 'Unavailable';
      return;
    }
    atc.disabled = false;
    if (label) label.textContent = C.atc || 'Add to cart';
  }

  function addToCart() {
    var v = currentVariant();
    var atc = dom.body.querySelector('[data-qv-atc]');
    var err = dom.body.querySelector('[data-qv-error]');
    var label = atc.querySelector('.qv-buy-label');
    if (err) err.hidden = true;
    if (!v) { if (err) { err.hidden = false; err.textContent = 'Please choose an option.'; } return; }
    if (!window.CartAPI || !window.CartAPI.addItem) {
      if (err) { err.hidden = false; err.textContent = 'Cart is unavailable. Please try again.'; }
      return;
    }
    atc.disabled = true;
    if (label) label.textContent = 'Adding…';
    // The theme's Cart API performs the add and dispatches cart:item-added /
    // cart:updated; the cart drawer opens itself. We only close QV on success.
    window.CartAPI.addItem(v.id, state.qty).then(function () {
      emit('quick_view_added_to_cart', { product_handle: state.product.handle, variant_id: v.id, quantity: state.qty, price: v.price });
      atc.disabled = false;
      paintTotal();
      close();
    }).catch(function () {
      atc.disabled = false;
      paintTotal();
      if (err) { err.hidden = false; err.textContent = 'Could not add to cart. Please try again.'; }
    });
  }

  // ---------------------------------------------------------------------------
  // Triggers (unchanged contract)
  // ---------------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    if (cfg().enabled === false) return;
    var t = e.target.closest('[data-quick-view]');
    if (t) {
      var handle = t.getAttribute('data-product-handle');
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      open(handle, 'quick_view_button');
      return;
    }
    var card = e.target.closest('[data-quick-view-card]');
    if (!card) return;
    // Let real controls behave normally.
    if (e.target.closest('[data-wishlist-btn], .product-card__color-swatches, .product-card__color-swatch, .product-card__actions, button, select, input, [data-no-quick-view]')) return;
    var h = card.getAttribute('data-product-handle');
    if (!h) return;
    e.preventDefault();
    open(h, 'card_tap');
  });

  document.addEventListener('keydown', function (e) {
    if (cfg().enabled === false) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var t = e.target.closest && e.target.closest('[data-quick-view]');
    if (!t) return;
    e.preventDefault();
    open(t.getAttribute('data-product-handle'), 'quick_view_button');
  });

  // Prefetch product JSON on intent so the panel opens instantly.
  function intentPrefetch(e) {
    var el = e.target.closest && e.target.closest('[data-quick-view-card],[data-quick-view]');
    if (!el) return;
    prefetch(el.getAttribute('data-product-handle'));
  }
  document.addEventListener('pointerover', intentPrefetch, { passive: true });
  document.addEventListener('focusin', intentPrefetch);
  document.addEventListener('touchstart', intentPrefetch, { passive: true });

  window.QuickView = { open: open, close: close };
})();
